import {
	Card,
	Button,
	Table,
	Tag,
	Space,
	Typography,
	Row,
	Col,
	Input,
	Select,
	DatePicker,
	notification,
	Badge,
	Tooltip,
	Popconfirm,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	PiCloudDuotone,
	PiEyeDuotone,
	PiMagnifyingGlassDuotone,
	PiFunnelDuotone,
	PiStopCircleDuotone,
	PiTrashDuotone,
	PiHardDrivesDuotone,
	PiPackageDuotone,
	PiMapPinDuotone,
	PiUserDuotone,
	PiReceiptDuotone,
	PiShoppingCartDuotone,
	PiHouseDuotone,
	PiCalendarDuotone,
} from "react-icons/pi";
import {
	requestGetInstanceList,
	requestSuspendInstance,
	requestTerminateInstance,
} from "../../services/admin.service";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

function InstanceListPage() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [instances, setInstances] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		search: searchParams.get("search") || "",
		status: searchParams.get("status") || "",
		serviceType: searchParams.get("serviceType") || "",
		dateRange: null,
	});
	const [suspendLoading, setSuspendLoading] = useState({});
	const [terminateLoading, setTerminateLoading] = useState({});

	useEffect(() => {
		document.title = "Cloudiva Console | Instances";
		fetchInstances();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.search) params.set("search", filters.search);
		if (filters.status) params.set("status", filters.status);
		if (filters.serviceType) params.set("serviceType", filters.serviceType);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchInstances = () => {
		setLoading(true);

		// Build filter string from filter object
		const filterParts = [];
		if (filters.status) {
			filterParts.push(`status:${filters.status}`);
		}
		if (filters.serviceType) {
			filterParts.push(`serviceType:${filters.serviceType}`);
		}
		// Add more filter fields here as needed

		const filterString = filterParts.join(",");

		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword || "",
			filter: filterString,
			order: "expiredAt:asc",
		};

		requestGetInstanceList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const instancesData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setInstances(instancesData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch((error) => {
				console.error("Error fetching instances:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch instances",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleTableChange = (paginationConfig) => {
		setPagination({
			...pagination,
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
		});
	};

	const handleSearch = (value) => {
		setFilters((prev) => ({ ...prev, search: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleFilterChange = (key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleSuspendInstance = (instanceId) => {
		setSuspendLoading((prev) => ({ ...prev, [instanceId]: true }));
		requestSuspendInstance(instanceId, { reason: "Suspended by admin" })
			.then(() => {
				notification.success({
					message: "Success",
					description: "Instance suspended successfully",
				});
				fetchInstances();
			})
			.catch((error) => {
				console.error("Error suspending instance:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to suspend instance",
				});
			})
			.finally(() => {
				setSuspendLoading((prev) => ({ ...prev, [instanceId]: false }));
			});
	};

	const handleTerminateInstance = (instanceId) => {
		setTerminateLoading((prev) => ({ ...prev, [instanceId]: true }));
		requestTerminateInstance(instanceId, { reason: "Terminated by admin" })
			.then(() => {
				notification.success({
					message: "Success",
					description: "Instance terminated successfully",
				});
				fetchInstances();
			})
			.catch((error) => {
				console.error("Error terminating instance:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to terminate instance",
				});
			})
			.finally(() => {
				setTerminateLoading((prev) => ({ ...prev, [instanceId]: false }));
			});
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "PENDING":
				return "orange";
			case "ACTIVE":
			case "RUNNING":
				return "green";
			case "SUSPENDED":
				return "red";
			case "TERMINATED":
				return "default";
			default:
				return "blue";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "PENDING":
				return "Pending";
			case "ACTIVE":
				return "Active";
			case "RUNNING":
				return "Running";
			case "SUSPENDED":
				return "Suspended";
			case "TERMINATED":
				return "Terminated";
			default:
				return status;
		}
	};

	const columns = [
		{
			title: "Instance",
			dataIndex: "number",
			key: "number",
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiCloudDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>
							ID: {record._id.substring(0, 8)}...
						</div>
					</div>
				</div>
			),
		},
		{
			title: "Service",
			dataIndex: ["service", "name"],
			key: "service",
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiPackageDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>
							Code: {record.service?.code} • Type: {record.type}
						</div>
					</div>
				</div>
			),
		},
		{
			title: "Plan",
			dataIndex: ["plan", "name"],
			key: "plan",
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{text}</div>
					<div style={{ fontSize: "12px", color: "#666" }}>
						{record.plan?.price?.amount && formatCurrency(record.plan.price.amount)}/
						{record.plan?.price?.interval}
					</div>
				</div>
			),
		},
		// {
		// 	title: "Region",
		// 	dataIndex: ["region", "name"],
		// 	key: "region",
		// 	width: 120,
		// 	render: (text, record) => (
		// 		<div style={{ display: "flex", alignItems: "center" }}>
		// 			<PiMapPinDuotone size={14} style={{ marginRight: 6, color: "#666" }} />
		// 			<div>
		// 				<div style={{ fontWeight: 500 }}>{text || "Global"}</div>
		// 				{record.region?.code && (
		// 					<div style={{ fontSize: "12px", color: "#666" }}>{record.region.code}</div>
		// 				)}
		// 			</div>
		// 		</div>
		// 	),
		// },
		{
			title: "Team",
			dataIndex: ["team", "name"],
			key: "team",
			render: (text, record) =>
				text ? (
					<div style={{ display: "flex", alignItems: "center" }}>
						<PiUserDuotone size={14} style={{ marginRight: 6, color: "#666" }} />
						<div>
							<div style={{ fontWeight: 500 }}>{text}</div>
							<div style={{ fontSize: "12px", color: "#666" }}>
								{record.team?.owner?.email?.substring(0, 20)}...
							</div>
						</div>
					</div>
				) : (
					<Text type="secondary">-</Text>
				),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status, record) => (
				<Space size={2} direction="vertical">
					<Tag color={getStatusColor(status)} style={{ borderRadius: "12px" }}>
						{getStatusText(status)}
					</Tag>
					{dayjs(record.expiredAt).isBefore(dayjs().add(60, "day")) && (
						<Tag color="#ff4d4f" style={{ borderRadius: "12px" }}>
							Expiring in {dayjs(record.expiredAt).diff(dayjs(), "day")} days
						</Tag>
					)}
				</Space>
			),
		},
		{
			title: "Dates",
			key: "dates",
			render: (_, record) => (
				<div style={{ fontSize: "12px" }}>
					<div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
						<PiCalendarDuotone size={12} style={{ marginRight: 4, color: "#666" }} />
						<span>Registered: {dayjs(record.registeredAt).format("MMM DD, YYYY")}</span>
					</div>
					<div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
						<PiCalendarDuotone size={12} style={{ marginRight: 4, color: "#ff7a45" }} />
						<span
							style={{
								color: dayjs(record.expiredAt).isBefore(dayjs().add(30, "day"))
									? "#ff4d4f"
									: "#666",
							}}>
							Expires: {dayjs(record.expiredAt).format("MMM DD, YYYY")}
						</span>
					</div>
					{record.nextBilledAt && (
						<div style={{ display: "flex", alignItems: "center" }}>
							<PiReceiptDuotone size={12} style={{ marginRight: 4, color: "#1890ff" }} />
							<span>Next Bill: {dayjs(record.nextBilledAt).format("MMM DD, YYYY")}</span>
						</div>
					)}
				</div>
			),
		},
		{
			title: "Actions",
			key: "actions",
			width: 80,
			fixed: "right",
			align: "right",
			render: (_, record) => (
				<Space>
					<Tooltip title="View Details">
						<Button
							type="text"
							icon={<PiEyeDuotone />}
							onClick={() => navigate(`/instances/${record._id}`)}
						/>
					</Tooltip>
					{record.status === "ACTIVE" && (
						<Tooltip title="Suspend Instance">
							<Popconfirm
								title="Suspend Instance"
								description="Are you sure you want to suspend this instance?"
								onConfirm={() => handleSuspendInstance(record._id)}
								okText="Yes, Suspend"
								cancelText="Cancel">
								<Button
									type="text"
									danger
									icon={<PiStopCircleDuotone />}
									loading={suspendLoading[record._id]}
								/>
							</Popconfirm>
						</Tooltip>
					)}
					{(record.status === "ACTIVE" || record.status === "SUSPENDED") && (
						<Tooltip title="Terminate Instance">
							<Popconfirm
								title="Terminate Instance"
								description="This action cannot be undone. Are you sure you want to terminate this instance?"
								onConfirm={() => handleTerminateInstance(record._id)}
								okText="Yes, Terminate"
								cancelText="Cancel">
								<Button
									type="text"
									danger
									icon={<PiTrashDuotone />}
									loading={terminateLoading[record._id]}
								/>
							</Popconfirm>
						</Tooltip>
					)}
				</Space>
			),
		},
	];

	const expandedRowRender = (record) => {
		return (
			<div style={{ padding: "16px", backgroundColor: "#fafafa", borderRadius: "8px" }}>
				<Row gutter={24}>
					<Col span={8}>
						<div style={{ marginBottom: "12px" }}>
							<Text strong style={{ display: "block", marginBottom: "4px" }}>
								Related Records
							</Text>
							<Space direction="vertical" size="small">
								{record.orderId && (
									<Button
										type="link"
										size="small"
										icon={<PiShoppingCartDuotone />}
										onClick={() => navigate(`/orders/${record.orderId}`)}>
										View Order
									</Button>
								)}
								{record.invoiceId && (
									<Button
										type="link"
										size="small"
										icon={<PiReceiptDuotone />}
										onClick={() => navigate(`/invoices/${record.invoiceId}`)}>
										View Invoice
									</Button>
								)}
							</Space>
						</div>
					</Col>
					<Col span={8}>
						<div style={{ marginBottom: "12px" }}>
							<Text strong style={{ display: "block", marginBottom: "4px" }}>
								Service Details
							</Text>
							<div style={{ fontSize: "12px" }}>
								<div style={{ marginBottom: "4px" }}>
									<Text type="secondary">Service Code: </Text>
									<Text>{record.service?.code}</Text>
								</div>
								<div style={{ marginBottom: "4px" }}>
									<Text type="secondary">Plan Code: </Text>
									<Text>{record.plan?.code}</Text>
								</div>
								<div style={{ marginBottom: "4px" }}>
									<Text type="secondary">Type: </Text>
									<Tag size="small">{record.type}</Tag>
								</div>
							</div>
						</div>
					</Col>
					<Col span={8}>
						<div>
							<Text strong style={{ display: "block", marginBottom: "4px" }}>
								Timestamps
							</Text>
							<div style={{ fontSize: "12px" }}>
								<div style={{ marginBottom: "4px" }}>
									<Text type="secondary">Created: </Text>
									<Text>{dayjs(record.createdAt).format("DD MMM YYYY, HH:mm")}</Text>
								</div>
								<div style={{ marginBottom: "4px" }}>
									<Text type="secondary">Updated: </Text>
									<Text>{dayjs(record.updatedAt).format("DD MMM YYYY, HH:mm")}</Text>
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</div>
		);
	};

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiHouseDuotone />} onClick={() => navigate("/")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Instance Management
					</Title>
					<Badge count={pagination.total} showZero></Badge>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Card
					title="All Instances"
					extra={
						<Space>
							<Search
								placeholder="Search instances..."
								allowClear
								value={filters.search}
								enterButton={<PiMagnifyingGlassDuotone />}
								style={{ width: 250 }}
								onSearch={handleSearch}
							/>
						</Space>
					}>
					{/* Filters */}
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={6}>
							<Select
								placeholder="Filter by status"
								allowClear
								value={filters.status || undefined}
								style={{ width: "100%" }}
								onChange={(value) => handleFilterChange("status", value)}
								suffixIcon={<PiFunnelDuotone />}>
								<Select.Option value="PENDING">Pending</Select.Option>
								<Select.Option value="ACTIVE">Active</Select.Option>
								<Select.Option value="RUNNING">Running</Select.Option>
								<Select.Option value="SUSPENDED">Suspended</Select.Option>
								<Select.Option value="TERMINATED">Terminated</Select.Option>
							</Select>
						</Col>
						<Col span={6}>
							<Select
								placeholder="Filter by service type"
								allowClear
								value={filters.serviceType || undefined}
								style={{ width: "100%" }}
								onChange={(value) => handleFilterChange("serviceType", value)}
								suffixIcon={<PiFunnelDuotone />}>
								<Select.Option value="WEHO">Web Hosting</Select.Option>
								<Select.Option value="VPS">VPS</Select.Option>
								<Select.Option value="DEDI">Dedicated Server</Select.Option>
								<Select.Option value="CLOUD">Cloud Computing</Select.Option>
							</Select>
						</Col>
						<Col span={6}>
							<RangePicker
								style={{ width: "100%" }}
								onChange={(dates) => handleFilterChange("dateRange", dates)}
								placeholder={["Start Date", "End Date"]}
							/>
						</Col>
					</Row>

					<Table
						columns={columns}
						dataSource={instances}
						rowKey="_id"
						loading={loading}
						pagination={{
							...pagination,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} instances`,
						}}
						onChange={handleTableChange}
						expandable={{
							expandedRowRender,
							rowExpandable: (record) => true,
						}}
						scroll={{ x: 1200 }}
						size="small"
					/>
				</Card>
			</div>
		</div>
	);
}

export default InstanceListPage;
